import { Injectable, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { AuthService } from '../../auth/services/auth.service';
import { InstanceConfigService } from '../../../core/services/instance-config.service';
import { SolicitudesWebService } from './solicitudes-web.service';

/** Quién puede abrir la bandeja: GESTION, porque hay datos personales. */
const ROLES = ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'];

/** Cada cuánto, como mucho, se vuelve a preguntar. */
const ESPERA_MINIMA_MS = 60_000;

/**
 * Cuántas solicitudes web están sin revisar, y si este tenant tiene el canal.
 *
 * <p>Alimenta el menú. Hace dos cosas con una sola consulta: el contador de «Solicitudes web»
 * y, con `habilitado`, decide si la entrada se muestra. Un tenant sin el canal (hoy, todos menos
 * Fixes) no ve una bandeja que nunca va a recibir nada.
 *
 * <p>Mismo criterio que `SolicitudesPendientesService`: se refresca al navegar, con un tope de un
 * minuto, y no con un temporizador.
 */
@Injectable({ providedIn: 'root' })
export class SolicitudesWebPendientesService {
  private servicio = inject(SolicitudesWebService);
  private auth = inject(AuthService);
  private instanceConfig = inject(InstanceConfigService);
  private router = inject(Router);

  private readonly _nuevas = signal<number>(0);
  private readonly _habilitado = signal<boolean>(false);

  /** Las que nadie revisó. `0` también significa «sin datos». */
  readonly cantidad = this._nuevas.asReadonly();

  /** El tenant tiene el canal encendido. Hasta que el servidor lo diga, es `false`. */
  readonly habilitado = this._habilitado.asReadonly();

  private ultimaConsulta = 0;
  private enVuelo = false;

  constructor() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.refrescar());

    // La configuración de instancia llega después del arranque; sin esto el menú esperaría a
    // la primera navegación para mostrar la entrada.
    effect(() => {
      if (this.instanceConfig.muestraServicioTecnico()) this.refrescar();
    });

    this.refrescar();
  }

  /** `forzar` lo usan las pantallas que acaban de cambiar el número. */
  refrescar(forzar = false): void {
    if (!this.puedeConsultar()) return;
    if (this.enVuelo) return;

    const ahora = Date.now();
    if (!forzar && ahora - this.ultimaConsulta < ESPERA_MINIMA_MS) return;

    this.ultimaConsulta = ahora;
    this.enVuelo = true;

    this.servicio.stats().subscribe({
      next: (res) => {
        this.enVuelo = false;
        if (res.success && res.data) {
          this._nuevas.set(res.data.nuevas ?? 0);
          this._habilitado.set(!!res.data.habilitado);
        }
      },
      error: () => { this.enVuelo = false; },
    });
  }

  /** Olvida lo aprendido al cerrar sesión: el siguiente usuario no hereda el tenant del anterior. */
  limpiar(): void {
    this._nuevas.set(0);
    this._habilitado.set(false);
    this.ultimaConsulta = 0;
  }

  private puedeConsultar(): boolean {
    return this.auth.isAuthenticated()
      && this.auth.hasAnyRole(ROLES)
      && this.instanceConfig.muestraServicioTecnico();
  }
}
